import { DOCUMENT } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, output, untracked } from '@angular/core';
import { themeConfettiColors } from '@core/theme/theme-confetti.utils';
import { OneThemeService } from '@one/services/one-theme.service';
import { toggleConfetti } from '@one/utils/confetti.utils';

export type TBookingConfirmationStatus = 'processing' | 'success' | 'error';

/**
 * - session: 401/403 — a sessão morreu; a tela permanece com um botão para solicitar novo acesso.
 * - generic: 400/5xx/rede — a tela permanece para o usuário tirar print e enviar ao estabelecimento.
 */
export type TBookingConfirmationErrorKind = 'session' | 'generic';

export interface IBookingConfirmationDetail {
  label: string;
  value: string;
}

/**
 * Tela fullscreen de confirmação do agendamento ou acesso pelo WhatsApp.
 *
 * Enquanto `status === 'processing'`, um campo de glows roxos deriva atrás do loader. Ao mudar para
 * `success`/`error`, o campo se dissipa (cada glow sai por um vetor próprio) e o card de status entra
 * com orbe, anéis da marca, ícone e estilhaços. O tempo mínimo de exibição e a troca de status são controlados
 * pelo pai, que conhece a requisição (revisão no app, confirmação ou acesso pelo WhatsApp).
 */
@Component({
  selector: 'agj-booking-confirmation',
  imports: [],
  templateUrl: './booking-confirmation.component.html',
  styleUrl: './booking-confirmation.component.scss'
})
export class BookingConfirmationComponent {
  public status = input.required<TBookingConfirmationStatus>();
  public isAccessRequest = input<boolean>(false);
  public errorKind = input<TBookingConfirmationErrorKind>('generic');
  public errorTitle = input<string>('Erro ao agendar');
  public errorCopy = input<string>('Tire um print desta tela e envie ao estabelecimento.');
  public successTitle = input<string>('Agendado');
  public successTitleLine2 = input<string>('com sucesso!');
  public details = input<IBookingConfirmationDetail[]>([]);
  public showBackButton = input<boolean>(false);
  public backLabel = input<string>('Voltar');
  public leaving = input<boolean>(false);
  public back = output<void>();
  public requestAccess = output<void>();

  public showRequestAccessButton = computed<boolean>(() => this.status() === 'error' && this.errorKind() === 'session');

  public isSuccess = computed<boolean>(() => this.status() === 'success');

  // Entrada dos glows vem de `@starting-style` no SCSS, para o campo já nascer em transição
  // sem esperar afterNextRender (aquele frame vazio no wash).
  public glowActive = computed<boolean>(() => this.status() === 'processing');
  public glowDissipating = computed<boolean>(() => this.status() !== 'processing');

  private destroyRef = inject(DestroyRef);
  private readonly oneTheme: OneThemeService = inject(OneThemeService);
  private readonly document: Document = inject(DOCUMENT);
  private confettiTimer = 0;

  constructor() {
    // Uma vez por entrada em sucesso: espera 1s e explode do centro. O timer não reseta em re-render.
    effect(() => {
      if (this.status() !== 'success') {
        if (this.confettiTimer) window.clearTimeout(this.confettiTimer);
        this.confettiTimer = 0;
        return;
      }

      if (this.confettiTimer) return;

      untracked(() => {
        // Cores lidas na hora do disparo: o confete segue o acento e o modo da empresa
        this.confettiTimer = window.setTimeout(() => toggleConfetti({ fromCenter: true, colors: themeConfettiColors(this.oneTheme.theme(), this.document) }), 1000);
      });
    });

    this.destroyRef.onDestroy(() => {
      if (this.confettiTimer) window.clearTimeout(this.confettiTimer);
    });
  }
}
